import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Breadcrumb, Button, Card, Column, Empty, Flex, Heading, Icon, Row, Spacer, Spinner, Text, Tree,
} from '@oliasoft-open-source/react-ui-library';
import translations from 'client/internationalization/translation-map.json';
import { HierarchyLevelType } from 'common/interfaces/hierarchy-entities';
import {
  IFilesBrowserItem, formatCountText, onOSFileDrag, toBreadcrumbLinks, toFolderPath, toTreeItems,
} from './files-browser.utils';
import {
  groupFoldersByParent, hasAvailableTarget, insertMoveToAction, isMovableItem, toMoveTargets,
} from './move-targets';
import { MoveToModal } from './move-to-modal';

type FilesBrowserProps = {
  heading: string;
  items: IFilesBrowserItem[];
  isLoading: boolean;
  rootParentNodeId?: string;
  onAddCatalogue: (parentNodeId?: string) => void;
  onUploadFile: (parentNodeId?: string) => void;
  onDropFiles: (files: File[], parentNodeId?: string) => void;
  onMoveItem: (item: IFilesBrowserItem, targetFolderId: string | number) => void;
};

export const FilesBrowser = ({
  heading,
  items,
  isLoading,
  rootParentNodeId,
  onAddCatalogue,
  onUploadFile,
  onDropFiles,
  onMoveItem,
}: FilesBrowserProps) => {
  const { t } = useTranslation();
  const [folderIds, setFolderIds] = useState<string[]>([]);
  const [itemToMove, setItemToMove] = useState<IFilesBrowserItem>();

  const folderPath = toFolderPath(folderIds, items);
  const currentFolderId = folderPath.length ? folderPath[folderPath.length - 1].id : 0;
  const effectiveParentNodeId = currentFolderId || rootParentNodeId;
  const currentLevelItems = items.filter((item) => item.parent === currentFolderId);

  const pathIds = folderPath.map((crumb) => crumb.id);
  const goToRoot = () => {
    setFolderIds([]);
  };
  const goBack = () => {
    setFolderIds(pathIds.slice(0, -1));
  };
  const goToCrumb = (i: number) => {
    setFolderIds(pathIds.slice(0, i + 1));
  };
  const openFolder = (item: IFilesBrowserItem) => {
    setFolderIds([...pathIds, String(item.id)]);
  };

  const handleDragOver = onOSFileDrag((event) => {
    event.preventDefault();
  });
  const handleDrop = onOSFileDrag((event) => {
    event.preventDefault();
    onDropFiles(Array.from(event.dataTransfer.files || []), effectiveParentNodeId);
  });

  const withIcon = (item: IFilesBrowserItem) => {
    const icon = item.entityType === HierarchyLevelType.Node ?  'folder' : 'file';
    const nameWithIcon = (
      <Flex alignItems="center" gap="var(--padding-xs)">
        <Icon icon={icon} size={15} />
        {item.name}
      </Flex>
    );
    return { ...item, name: nameWithIcon };
  };

  const hasItems = currentLevelItems.length > 0;
  const showEmptyState = !hasItems && !isLoading;
  const catalogueCount = currentLevelItems.filter((item) => item.entityType === HierarchyLevelType.Node).length;
  const fileCount = currentLevelItems.filter((item) => item.entityType === HierarchyLevelType.Object).length;
  const countText = formatCountText(catalogueCount, fileCount, t);
  const breadcrumbLinks = toBreadcrumbLinks(folderPath, t(translations.files), goToRoot, goToCrumb);

  const foldersByParent = groupFoldersByParent(items);
  const getMoveTargets = (item: IFilesBrowserItem) => {
    return toMoveTargets({
      foldersByParent,
      folderPath,
      rootLabel: t(translations.files),
      canMoveToRoot: item.entityType === HierarchyLevelType.Node || !!rootParentNodeId,
      movedItem: item,
    });
  };

  const canMove = (item: IFilesBrowserItem) => {
    return isMovableItem(item) && hasAvailableTarget(getMoveTargets(item));
  };

  const toMoveToAction = (item: IFilesBrowserItem) => {
    return {
      type: 'Option',
      icon: 'folder',
      label: t(translations.moveTo),
      onClick: () => setItemToMove(item),
    };
  };

  const withMoveToAction = (item: IFilesBrowserItem) => {
    const itemWithIcon = withIcon(item);
    if (!canMove(item)) {
      return itemWithIcon;
    }
    return { ...itemWithIcon, actions: insertMoveToAction(item.actions, toMoveToAction(item)) };
  };

  return (
    <div onDragOver={handleDragOver} onDrop={handleDrop}>
      <Row spacing={0}>
        <Column>
          <Heading>
            {heading}
            <Spacer width="var(--padding-xxs)" />
            {isLoading && <Flex justifyContent="center"><Spinner dark tiny /></Flex>}
          </Heading>
        </Column>
        <Button
          label={`${t(translations.add)} ${t(translations.catalogue)}`}
          onClick={() => onAddCatalogue(effectiveParentNodeId)}
          small
        />
        <Flex alignItems="center">
          <Spacer width="var(--padding)" />
          <Button
            colored
            label={t(translations.importExport_uploadFile)}
            onClick={() => onUploadFile(effectiveParentNodeId)}
            small
          />
        </Flex>
      </Row>
      <Spacer />
      
      <Card padding={false} heading={(
        <Flex width="100%" justifyContent="space-between" alignItems="center">
          <Flex alignItems="center" gap="var(--padding-xs)">
            <Button small round basic colored="muted" onClick={goBack} icon="left" disabled={folderPath.length === 0} />
            <Breadcrumb small links={breadcrumbLinks} />
          </Flex>
          {countText && <Text faint>{countText}</Text>}
        </Flex>
      )}>
        {hasItems && (
          <Tree
            list={{
              items: toTreeItems(currentLevelItems, openFolder).map(withMoveToAction),
            }}
            itemHasChild={() => false}
          />
        )}
        {showEmptyState && (
          <Flex justifyContent="center" alignItems="center">
            <Empty height="160px" text={(
              <>
                <Text>{t(translations.importExport_noFilesUploadedYet)}</Text>
                {' '}
                <Button
                  colored
                  small
                  label={t(translations.importExport_uploadFile)}
                  onClick={() => onUploadFile(effectiveParentNodeId)}
                />
              </>
            )}/>
          </Flex>
        )}
      </Card>
      <Spacer />
      {itemToMove && (
        <MoveToModal
          item={itemToMove}
          targets={getMoveTargets(itemToMove)}
          onMove={onMoveItem}
          onClose={() => setItemToMove(undefined)}
        />
      )}
    </div>
  );
};
